import { pbiBand, PBI_ADEQUATE_MIN } from "@/lib/pbi-bands"

import { alternateFormAsk, alternateFormReason, shouldOfferAlternateForm } from "./leo-jobs"
import { performanceInsightFor } from "./performance-insight"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualityHistory,
  QuestionQualitySnapshot,
} from "./types"
import { LEO_ENHANCE_WITH_LEO_LABEL } from "./leo-cta-labels"
import { VARIATION_SET_CHECK_ID } from "./variation-ask"

export const PERFORMANCE_USAGE_CHECK_ID = "performance-usage"
export const PERFORMANCE_PBI_CHECK_ID = "performance-pbi"
export const PERFORMANCE_DIFFICULTY_CHECK_ID = "performance-difficulty"
export const PERFORMANCE_DISC_CHECK_ID = "performance-discrimination"

export const PERFORMANCE_REPLACE_ALTERNATE_IDS = new Set([
  "performance-usage-replace",
  "performance-pbi-replace",
])

export function isPerformanceCheckId(id: string): boolean {
  return (
    id === PERFORMANCE_USAGE_CHECK_ID ||
    id === PERFORMANCE_PBI_CHECK_ID ||
    id === PERFORMANCE_DIFFICULTY_CHECK_ID ||
    id === PERFORMANCE_DISC_CHECK_ID
  )
}

export function isPerformanceReplaceAlternate(id: string): boolean {
  return PERFORMANCE_REPLACE_ALTERNATE_IDS.has(id)
}

export type PerformanceIssueHeaderCopy = {
  before: string
  highlight: string
  after: string
}

/** Inline stat for chips and compact labels. */
export function performanceIssueLabel(
  check: QuestionQualityCheck,
  history: QuestionQualityHistory,
): string | null {
  return performanceIssueHeaderCopy(check, history)?.highlight ?? null
}

/**
 * Pager header: plain lead + highlighted stat + crisp consequence.
 * The pill wraps `highlight` only; before/after justify why it matters.
 */
export function performanceIssueHeaderCopy(
  check: QuestionQualityCheck,
  history: QuestionQualityHistory,
): PerformanceIssueHeaderCopy | null {
  switch (check.id) {
    case PERFORMANCE_USAGE_CHECK_ID:
      if (!history.usedCount || history.usedCount <= 0) return null
      return {
        before: "Used ",
        highlight: `${history.usedCount}× in course`,
        after: ". Returning students may recognize the wording.",
      }
    case PERFORMANCE_PBI_CHECK_ID:
      if (history.pointBiserial == null) return null
      return {
        before: "Point-biserial ",
        highlight: `PBI ${history.pointBiserial.toFixed(2)}`,
        after: " is below the review floor.",
      }
    case PERFORMANCE_DIFFICULTY_CHECK_ID:
      if (history.measuredDifficultyPercent == null) return null
      return {
        before: "Examinees scored ",
        highlight: `${history.measuredDifficultyPercent}% correct`,
        after: ", not the tagged difficulty.",
      }
    case PERFORMANCE_DISC_CHECK_ID:
      if (history.discriminationIndex == null) return null
      return {
        before: "Discrimination ",
        highlight: `Disc. ${history.discriminationIndex.toFixed(2)}`,
        after: " is weak on this item.",
      }
    default:
      return null
  }
}

function performanceIssueTitle(
  checkId: string,
  history: QuestionQualityHistory,
): string {
  const stub = { id: checkId } as QuestionQualityCheck
  const copy = performanceIssueHeaderCopy(stub, history)
  if (!copy) return "Performance signal"
  return `${copy.before}${copy.highlight}${copy.after}`
}

function usageAlternates(): QuestionQualityAlternate[] {
  return [
    {
      id: "performance-usage-variation",
      text: "Keep the same blueprint cell. Write a sibling item with new wording and options.",
      actionKind: "open-leo",
      actionLabel: "See a different variation",
      parameterDeltas: [{ parameter: "exposure", effect: "clears" }],
      clearsCheckIds: [PERFORMANCE_USAGE_CHECK_ID],
    },
    {
      id: "performance-usage-replace",
      text: "Swap this item for another question from the bank that fits the same blueprint cell.",
      actionKind: "none",
      actionLabel: "Replace with another question",
      parameterDeltas: [],
      clearsCheckIds: [PERFORMANCE_USAGE_CHECK_ID],
    },
  ]
}

function pbiAlternates(): QuestionQualityAlternate[] {
  return [
    {
      id: "performance-pbi-enhance",
      text: "Tighten the stem and distractors so strong and weak examinees split more clearly.",
      actionKind: "open-leo",
      actionLabel: LEO_ENHANCE_WITH_LEO_LABEL,
      parameterDeltas: [{ parameter: "discrimination", effect: "clears" }],
      clearsCheckIds: [PERFORMANCE_PBI_CHECK_ID],
    },
    {
      id: "performance-pbi-replace",
      text: "Swap this item for a stronger question from the bank.",
      actionKind: "none",
      actionLabel: "Replace with another question",
      parameterDeltas: [],
      clearsCheckIds: [PERFORMANCE_PBI_CHECK_ID],
    },
  ]
}

function difficultyAlternates(
  measuredBand: string,
): QuestionQualityAlternate[] {
  return [
    {
      id: "performance-difficulty-retag",
      text: `Retag difficulty to ${measuredBand} to match how examinees answered.`,
      actionKind: "set-difficulty",
      actionLabel: `Set difficulty to ${measuredBand}`,
      patch: { difficulty: measuredBand },
      parameterDeltas: [{ parameter: "difficulty", effect: "clears" }],
      clearsCheckIds: [PERFORMANCE_DIFFICULTY_CHECK_ID],
    },
    {
      id: "performance-difficulty-variation",
      text: "Keep the blueprint cell. Write a sibling item at the measured difficulty.",
      actionKind: "open-leo",
      actionLabel: "See a different variation",
      parameterDeltas: [{ parameter: "difficulty", effect: "clears" }],
      clearsCheckIds: [PERFORMANCE_DIFFICULTY_CHECK_ID],
    },
  ]
}

function discriminationAlternates(): QuestionQualityAlternate[] {
  return [
    {
      id: "performance-disc-variation",
      text: "Rewrite the stem or options so the key separates strong from weak examinees.",
      actionKind: "open-leo",
      actionLabel: "See a different variation",
      parameterDeltas: [{ parameter: "discrimination", effect: "clears" }],
      clearsCheckIds: [PERFORMANCE_DISC_CHECK_ID],
    },
    {
      id: "performance-disc-options",
      text: "Keep the stem. Write a fresh distractor set for the same key.",
      actionKind: "open-leo",
      actionLabel: "Swap distractors",
      parameterDeltas: [{ parameter: "discrimination", effect: "clears" }],
      clearsCheckIds: [PERFORMANCE_DISC_CHECK_ID],
    },
  ]
}

function measuredDifficultyBand(percent: number): "easy" | "medium" | "hard" {
  if (percent >= 70) return "easy"
  if (percent >= 45) return "medium"
  return "hard"
}

function performanceMechanism(
  history: QuestionQualityHistory,
  lead: string,
): string {
  const insight = performanceInsightFor(history)
  const cohort =
    insight?.examineeCount != null
      ? ` Based on ${insight.examineeCount} examinees.`
      : ""
  return `${lead}${cohort}`
}

/**
 * Exam-history suggestions for Ask Leo. Appended after quality checks so
 * authors see usage, PBI, and difficulty evidence beside rubric findings.
 */
export function performanceSuggestionChecks(
  history: QuestionQualityHistory | null | undefined,
  snapshot: QuestionQualitySnapshot,
  qualityChecks: QuestionQualityCheck[],
): QuestionQualityCheck[] {
  if (!history?.sat) return []

  const existing = new Set(qualityChecks.map((check) => check.id))
  const rows: QuestionQualityCheck[] = []

  if (
    history.belowReviewFloor &&
    !existing.has(PERFORMANCE_PBI_CHECK_ID) &&
    !existing.has("classification-discrimination")
  ) {
    const pbi = history.pointBiserial?.toFixed(2) ?? "—"
    rows.push({
      id: PERFORMANCE_PBI_CHECK_ID,
      parameter: "discrimination",
      severity: "review",
      field: "leadIn",
      title: performanceIssueTitle(PERFORMANCE_PBI_CHECK_ID, history),
      mechanism: performanceMechanism(
        history,
        `Point-biserial ${pbi} is below the review floor, so this item does not separate examinees well.`,
      ),
      rubricConfidence: "high",
      actionKind: "open-leo",
      actionLabel: LEO_ENHANCE_WITH_LEO_LABEL,
      alternates: pbiAlternates(),
    })
  }

  if (
    shouldOfferAlternateForm(history) &&
    !existing.has(PERFORMANCE_USAGE_CHECK_ID) &&
    !existing.has(VARIATION_SET_CHECK_ID)
  ) {
    rows.push({
      id: PERFORMANCE_USAGE_CHECK_ID,
      parameter: "exposure",
      severity: "caution",
      field: "leadIn",
      title: performanceIssueTitle(PERFORMANCE_USAGE_CHECK_ID, history),
      mechanism: performanceMechanism(
        history,
        `This item was ${alternateFormReason(history).toLowerCase()}. Returning students may recognize the wording.`,
      ),
      rubricConfidence: "high",
      actionKind: "open-leo",
      actionLabel: "See a different variation",
      alternates: usageAlternates(),
    })
  }

  if (
    history.measuredDifficultyPercent != null &&
    history.taggedDifficulty &&
    history.difficultyMatches === false &&
    !existing.has(PERFORMANCE_DIFFICULTY_CHECK_ID) &&
    !existing.has("classification-difficulty")
  ) {
    const measuredBand = measuredDifficultyBand(history.measuredDifficultyPercent)
    rows.push({
      id: PERFORMANCE_DIFFICULTY_CHECK_ID,
      parameter: "difficulty",
      severity: "caution",
      field: "settings",
      settingsTarget: "difficulty",
      title: performanceIssueTitle(PERFORMANCE_DIFFICULTY_CHECK_ID, history),
      mechanism: performanceMechanism(
        history,
        `Tagged ${history.taggedDifficulty.toLowerCase()}. Measured ${history.measuredDifficultyPercent}% correct, so examinees found it ${measuredBand}.`,
      ),
      rubricConfidence: "medium",
      actionKind: "set-difficulty",
      actionLabel: `Set difficulty to ${measuredBand}`,
      alternates: difficultyAlternates(measuredBand),
    })
  }

  if (
    rows.length === 0 &&
    !history.belowReviewFloor &&
    (history.pointBiserial ?? 1) < PBI_ADEQUATE_MIN &&
    !existing.has(PERFORMANCE_DISC_CHECK_ID)
  ) {
    const band = pbiBand(history.pointBiserial)
    rows.push({
      id: PERFORMANCE_DISC_CHECK_ID,
      parameter: "discrimination",
      severity: "caution",
      field: "leadIn",
      title: performanceIssueTitle(PERFORMANCE_DISC_CHECK_ID, history),
      mechanism: performanceMechanism(
        history,
        `${band.label}. Strong and weak examinees answered similarly on this item.`,
      ),
      rubricConfidence: "medium",
      actionKind: "open-leo",
      actionLabel: "See a different variation",
      alternates: discriminationAlternates(),
    })
  }

  return rows
}

export function performanceVariationAsk(
  check: QuestionQualityCheck,
  history: QuestionQualityHistory | null | undefined,
): string {
  if (check.id === PERFORMANCE_USAGE_CHECK_ID) {
    return alternateFormAsk(history)
  }
  if (check.id === PERFORMANCE_DIFFICULTY_CHECK_ID) {
    return "Draft another form of this item at the measured difficulty. Keep the same blueprint cell."
  }
  if (check.id === PERFORMANCE_DISC_CHECK_ID) {
    return "Give me a variation that separates strong from weak examinees better. Keep the same blueprint cell."
  }
  return alternateFormAsk(history)
}
